/**
 * useCursorParallax
 *
 * Single rAF loop that lerps a shared cursor position toward the real pointer.
 * Consumers pass React refs (not bare elements) so that the effect can read
 * the live `.current` after mount.
 *
 * Design principles:
 *  - ONE pointermove listener per hook instance, ONE rAF loop (no per-frame
 *    setState — React is never involved after mount).
 *  - Respects `prefers-reduced-motion: reduce` — the entire loop is a no-op.
 *  - Touch / no-hover guard: if the primary pointing device can't hover, we
 *    never start the loop.
 *  - All transforms are translate3d — GPU compositor only, no layout thrash.
 */

import { useEffect, useRef, RefObject } from 'react';

// ─── types ────────────────────────────────────────────────────────────────────

/** A single layer the rAF loop should move. */
export interface ParallaxLayer {
  /** React ref to the DOM element to transform. */
  ref: RefObject<HTMLElement | null>;
  /**
   * Depth coefficient.
   * Positive = moves WITH cursor, negative = moves AGAINST (counter-parallax).
   * Magnitude controls travel distance (1.0 ≈ maxTravel px).
   */
  depth: number;
  /**
   * Max pixels of travel (default 12).
   * Final offset = depth * maxTravel * normalised cursor offset.
   */
  maxTravel?: number;
}

export interface SpotlightConfig {
  /** React ref to the element that gets the radial-gradient spotlight. */
  ref: RefObject<HTMLElement | null>;
  /** Colour stop — e.g. "rgba(13,148,136,0.18)" */
  color?: string;
  /** Spotlight radius in px (default 520). */
  radius?: number;
}

// ─── constants ────────────────────────────────────────────────────────────────

const LERP_FACTOR = 0.08; // smoothing: 0 = frozen, 1 = instant

// ─── hook ─────────────────────────────────────────────────────────────────────

interface UseCursorParallaxOptions {
  /**
   * The container element whose bounding box is used to normalise the pointer
   * position. Usually the hero section.
   */
  containerRef: RefObject<HTMLElement | null>;
  layers: ParallaxLayer[];
  spotlight?: SpotlightConfig;
}

export function useCursorParallax({
  containerRef,
  layers,
  spotlight,
}: UseCursorParallaxOptions): void {
  // These refs hold mutable loop state and never trigger re-renders.
  const rawRef   = useRef({ x: 0.5, y: 0.5 }); // normalised [0…1]
  const lerpRef  = useRef({ x: 0.5, y: 0.5 }); // smoothed
  const rafRef   = useRef<number>(0);
  const activeRef = useRef(false);

  useEffect(() => {
    // ── Guards ────────────────────────────────────────────────────────────────
    const reducedMotion =
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const noHover =
      window.matchMedia('(hover: none)').matches ||
      window.matchMedia('(pointer: coarse)').matches;

    if (reducedMotion || noHover) return; // static for a11y + touch

    activeRef.current = true;

    // ── Pointer listener ─────────────────────────────────────────────────────
    function onPointerMove(e: PointerEvent) {
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      rawRef.current.x = (e.clientX - rect.left) / rect.width;
      rawRef.current.y = (e.clientY - rect.top)  / rect.height;
    }

    window.addEventListener('pointermove', onPointerMove, { passive: true });

    // ── rAF loop ──────────────────────────────────────────────────────────────
    function lerp(a: number, b: number, t: number) {
      return a + (b - a) * t;
    }

    function tick() {
      if (!activeRef.current) return;

      // Lerp smoothed position toward raw
      lerpRef.current.x = lerp(lerpRef.current.x, rawRef.current.x, LERP_FACTOR);
      lerpRef.current.y = lerp(lerpRef.current.y, rawRef.current.y, LERP_FACTOR);

      // Centred offset: -0.5 … +0.5
      const ox = lerpRef.current.x - 0.5;
      const oy = lerpRef.current.y - 0.5;

      // ── Parallax layers ───────────────────────────────────────────────────
      for (const layer of layers) {
        const el = layer.ref.current;
        if (!el) continue;
        const travel = layer.maxTravel ?? 12;
        const tx = ox * layer.depth * travel;
        const ty = oy * layer.depth * travel;
        el.style.transform = `translate3d(${tx.toFixed(2)}px,${ty.toFixed(2)}px,0)`;
        el.style.willChange = 'transform';
      }

      // ── Spotlight ─────────────────────────────────────────────────────────
      if (spotlight) {
        const el = spotlight.ref.current;
        if (el) {
          const r     = spotlight.radius ?? 520;
          const color = spotlight.color  ?? 'rgba(13,148,136,0.13)';
          const px = (lerpRef.current.x * 100).toFixed(1);
          const py = (lerpRef.current.y * 100).toFixed(1);
          el.style.background =
            `radial-gradient(${r}px circle at ${px}% ${py}%, ${color}, transparent 70%)`;
          el.style.willChange = 'background';
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    }

    rafRef.current = requestAnimationFrame(tick);

    // ── Cleanup ───────────────────────────────────────────────────────────────
    return () => {
      activeRef.current = false;
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('pointermove', onPointerMove);

      // Reset transforms on unmount
      for (const layer of layers) {
        const el = layer.ref.current;
        if (el) el.style.transform = '';
      }
      if (spotlight?.ref.current) {
        spotlight.ref.current.style.background = '';
      }
    };
    // We intentionally only run once on mount. Refs hold live values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
